import {
  ArrowBack,
  BarChart,
  BusinessCenter,
  ContentCopy,
  Devices,
  ImportExport,
  Lock,
  MenuBook,
  Psychology,
  Speed,
  Support,
  SyncProblem,
  WorkspacePremium,
} from '@mui/icons-material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import Grid2 from '@mui/material/Grid';
import { motion } from 'framer-motion';
import React from 'react';
import { toast } from 'react-toastify';

import { MotionWrapper } from '@/components/motionWrapper/MotionWrapper.comp.tsx';
import { ToastConfig } from '@/config/Toastify.config.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useKnowMeNavigate } from '@/utils/useKnowMeNavigation.util.ts';
import { useTranslationArray } from '@/utils/useTranslationArray.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { ContactCard } from '@/views/contact/components/ContactCard.comp.tsx';
import { JERONIMO_EMAIL, KNOWME_EMAIL } from '@/views/contact/const/constants.ts';

const ContactView: React.FC = () => {
  const { t } = useTranslationWithPrefix('views.contact');
  const { push } = useKnowMeNavigate();
  const theme = useTheme();

  const knowmeCardItems = useTranslationArray('views.contact.knowmeCard.bulletPoints').map(
    (label, index) => ({
      icon: [BugReportIcon, Lock, SyncProblem, Speed, Devices, ImportExport][index],
      label,
    }),
  );

  const jeronimoCardItems = useTranslationArray('views.contact.jeronimoCard.bulletPoints').map(
    (label, index) => ({
      icon: [Psychology, WorkspacePremium, BarChart, MenuBook][index],
      label,
    }),
  );

  const copyEmailToClipboard = async (email: string) => {
    await navigator.clipboard.writeText(email);
    toast(t('copySuccess'), ToastConfig.success);
  };

  return (
    <Box
      sx={{
        background: theme.colors.bg,
        color: theme.colors.textPrimary,
        height: '100dvh',
        overflowX: 'hidden',
        position: 'relative',
        px: { md: 6, xs: 3 },
        py: { md: 5, xs: 3 },
        width: '100%',
      }}
    >
      <Box sx={{ maxWidth: 1440, mt: 14, mx: 'auto', width: '100%' }}>
        <Stack
          sx={{
            alignItems: 'flex-start',
            gap: 5,
            height: '100%',
            width: '100%',
          }}
        >
          <Stack
            sx={{
              alignItems: 'flex-start',
              gap: 1,
              height: '100%',
              width: '100%',
            }}
          >
            <Button
              onClick={() => push(RouteEnum.ROOT)}
              startIcon={<ArrowBack />}
              sx={{
                '&:hover': {
                  background: 'transparent',
                  color: theme.colors.accentInk,
                  transform: 'translateX(-4px)',
                },
                '& svg': {
                  fontSize: '18px !important',
                },
                color: theme.colors.textSecondary,
                fontSize: 13,
                fontWeight: 600,
                minHeight: 32,
                p: 0,
                transition: 'color 0.2s ease, transform 0.2s ease',
              }}
              variant="text"
            >
              {t('backToStart')}
            </Button>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <Typography
                sx={{
                  color: theme.colors.textPrimary,
                  fontSize: 32,
                  fontWeight: 600,
                  letterSpacing: 0,
                  lineHeight: 1.1,
                  textAlign: 'left',
                }}
              >
                {t('title')}
              </Typography>
            </motion.div>
          </Stack>

          <Grid2 container spacing={{ md: 4, xs: 2.5 }} sx={{ width: '100%' }}>
            <Grid2 size={{ lg: 6, md: 12, xs: 12 }}>
              <MotionWrapper>
                <ContactCard
                  bulletPoints={knowmeCardItems}
                  buttonIcon={<ContentCopy />}
                  buttonText={KNOWME_EMAIL}
                  description={t('knowmeCard.subtitle')}
                  icon={Support}
                  onButtonClick={() => copyEmailToClipboard(KNOWME_EMAIL)}
                  title={t('knowmeCard.title')}
                />
              </MotionWrapper>
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, xs: 12 }}>
              <MotionWrapper>
                <ContactCard
                  bulletPoints={jeronimoCardItems}
                  buttonIcon={<ContentCopy />}
                  buttonText={JERONIMO_EMAIL}
                  description={t('jeronimoCard.subtitle')}
                  icon={BusinessCenter}
                  onButtonClick={() => copyEmailToClipboard(JERONIMO_EMAIL)}
                  title={t('jeronimoCard.title')}
                />
              </MotionWrapper>
            </Grid2>
          </Grid2>
        </Stack>
      </Box>
    </Box>
  );
};

export default ContactView;
